/**
 * Product Video Gallery Widget
 * Displays product videos in carousel format (same pattern as personalization carousel)
 *
 * Features:
 * - Pulls videos from product media (uploaded, YouTube, Vimeo)
 * - Extracts embedded videos from product description
 * - Orders videos with assembly videos last
 * - Carousel UI with scroll-snap (desktop shows all if ≤3, mobile carousel)
 * - Auto-hides if no videos available
 * - British English throughout
 * - Theme token styling
 *
 * @version 1.0.0
 */

(function(window) {
  'use strict';

  class VideoGalleryWidget extends window.ProductWidgets.BaseWidget {
    constructor(manager, container, config) {
      super(manager, container, config);

      this.videos = [];
      this.modalId = `video-gallery-modal-${config.id}`;
      this.currentSlide = 0;
      this.isAutoPlaying = false; // Disabled autoplay
      this.autoPlayInterval = null;
      this.autoPlayDelay = 5000; // 5 seconds per slide

      // Content (can be customised via block settings)
      this.content = {
        cardHeading: config.cardHeading || 'Product Videos',
        cardSubheading: config.cardSubheading || 'Watch demos and assembly guides',
        viewButton: config.viewButton || 'Watch Videos',
        modalHeading: config.modalHeading || 'Product Videos',
        modalSubheading: config.modalSubheading || 'Watch product demonstrations and assembly guides'
      };
    }

    /**
     * Initialize widget - compile videos from all sources
     */
    async init() {
      console.log('[VideoGallery] Init - Config received:', {
        productMediaVideos: this.config.productMediaVideos?.length || 0,
        descriptionVideos: this.config.descriptionVideos?.length || 0,
        showcaseIds: this.config.showcaseIds || []
      });

      // Collect videos from config (passed from Liquid)
      const productMediaVideos = this.config.productMediaVideos || [];
      let descriptionVideos = this.config.descriptionVideos || [];

      // Fetch showcase videos from Lambda if any showcases detected
      const showcaseIds = this.config.showcaseIds || [];
      console.log('[VideoGallery] Showcase IDs to fetch:', showcaseIds);

      if (showcaseIds.length > 0) {
        console.log('[VideoGallery] Calling Lambda for showcases...');
        const showcaseVideos = await this.fetchShowcaseVideos(showcaseIds);
        console.log('[VideoGallery] Lambda returned videos:', showcaseVideos);
        descriptionVideos = [...descriptionVideos, ...showcaseVideos];
      }

      // Combine and deduplicate videos
      const allVideos = [...productMediaVideos, ...descriptionVideos];
      console.log('[VideoGallery] Total videos before deduplication:', allVideos.length);

      // Remove duplicates based on video ID
      const uniqueVideos = [];
      const seenIds = new Set();

      allVideos.forEach(video => {
        const id = video.videoId || video.id || video.url;
        if (id && !seenIds.has(id)) {
          seenIds.add(id);
          uniqueVideos.push(video);
        }
      });

      // Sort: Assembly videos last, others maintain order
      this.videos = uniqueVideos.sort((a, b) => {
        const aIsAssembly = this.isAssemblyVideo(a);
        const bIsAssembly = this.isAssemblyVideo(b);

        if (aIsAssembly && !bIsAssembly) return 1;  // a goes after b
        if (!aIsAssembly && bIsAssembly) return -1; // a goes before b
        return 0; // maintain original order
      });

      console.log('[VideoGallery] Final videos to display:', this.videos.length, this.videos);

      // Hide widget if no videos
      if (this.videos.length === 0) {
        console.log('[VideoGallery] No videos found - hiding widget');
        this.hide();
        return;
      }

      console.log('[VideoGallery] Init complete - ready to render');
    }

    /**
     * Fetch individual videos from Vimeo showcases via Lambda
     */
    async fetchShowcaseVideos(showcaseIds) {
      const lambdaUrl = 'https://fu34e5wbiiblekzbqalssbbwa40syefe.lambda-url.eu-west-2.on.aws/';
      const videos = [];

      for (const showcaseId of showcaseIds) {
        try {
          const response = await fetch(`${lambdaUrl}?showcaseId=${showcaseId}`);

          if (!response.ok) {
            console.error(`[VideoGallery] Lambda error for showcase ${showcaseId}:`, response.status);
            continue;
          }

          const data = await response.json();

          if (data.videos && Array.isArray(data.videos)) {
            // Convert Lambda response to widget format
            data.videos.forEach(video => {
              videos.push({
                type: 'vimeo',
                videoId: video.videoId,
                title: video.title || 'Product Video',
                thumbnail: video.thumbnail,
                duration: video.duration,
                source: 'showcase'
              });
            });

            console.log(`[VideoGallery] Loaded ${data.videos.length} videos from showcase ${showcaseId}`);
          }

        } catch (error) {
          console.error(`[VideoGallery] Failed to fetch showcase ${showcaseId}:`, error);
        }
      }

      return videos;
    }

    /**
     * Check if video is assembly-related based on title/alt
     */
    isAssemblyVideo(video) {
      const searchText = (video.title || video.alt || '').toLowerCase();
      return searchText.includes('assembly') ||
             searchText.includes('assemble') ||
             searchText.includes('how to build') ||
             searchText.includes('installation');
    }

    /**
     * Render widget to container
     */
    render() {
      // Don't render if no videos
      if (!this.videos || this.videos.length === 0) {
        this.hide();
        return;
      }

      // Build carousel (same pattern as personalization carousel)
      const html = `
        ${this.buildCardHTML()}
        ${this.buildModalHTML()}
      `;

      this.setContent(html);
      this.attachEventListeners();

      this.emit('video-gallery:rendered');
    }

    /**
     * Build carousel widget (same pattern as personalization carousel)
     * Shows up to 3 video thumbnail cards
     */
    buildCardHTML() {
      // Dark theme colors (like personalization carousel)
      const bgColor = 'rgb(var(--color-drawer-text))';
      const textColor = 'rgb(var(--color-drawer-background))';
      const mutedColor = 'rgb(var(--color-drawer-background) / 0.7)';

      // Only show first 3 videos in carousel
      const carouselVideos = this.videos.slice(0, 3);

      return `
        <div style="background: ${bgColor}; border-radius: var(--rounded-card); padding: var(--sp-5); position: relative; overflow: hidden;">

          <!-- Header -->
          <div style="display: flex; align-items: flex-start; gap: var(--sp-3); margin-bottom: var(--sp-5);">
            <div style="display: flex; align-items: center; justify-content: center; width: 2.5rem; height: 2.5rem; background: rgb(var(--color-drawer-background) / 0.15); border-radius: var(--rounded-card); flex-shrink: 0;">
              <svg style="width: 1.25rem; height: 1.25rem; color: ${textColor};" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/>
              </svg>
            </div>
            <div>
              <h3 style="font-family: var(--font-heading-family); font-weight: var(--font-heading-weight); font-size: var(--text-lg); line-height: 1.2; margin: 0 0 var(--sp-1) 0; color: ${textColor};">
                ${this.content.cardHeading}
              </h3>
              <p style="font-size: var(--text-sm); line-height: 1.5; color: ${mutedColor}; margin: 0;">
                ${this.content.cardSubheading}
              </p>
            </div>
          </div>

          <!-- Video Carousel Track -->
          <div class="pw-video-carousel-track" role="region" aria-label="Product videos carousel" style="display: flex; overflow-x: auto; overflow-y: hidden; scroll-snap-type: x mandatory; scroll-behavior: smooth; gap: var(--sp-4); border-radius: var(--rounded-card); margin-bottom: var(--sp-4); -webkit-overflow-scrolling: touch; scrollbar-width: none; -ms-overflow-style: none;">
            ${carouselVideos.map((video, index) => this.renderVideoCard(video, index)).join('')}
          </div>

          <!-- Controls Row (dots + view all) - dots hidden on desktop -->
          <div style="display: flex; align-items: center; justify-content: center; gap: var(--sp-4);">
            <!-- Dots Indicator (Mobile only) -->
            <div class="pw-video-carousel-dots" role="tablist" aria-label="Video navigation" style="display: none; gap: var(--sp-2); align-items: center;">
              ${carouselVideos.map((_, index) => `
                <button
                  class="pw-video-carousel-dot ${index === 0 ? 'pw-video-carousel-dot--active' : ''}"
                  role="tab"
                  aria-label="Go to video ${index + 1}"
                  aria-selected="${index === 0}"
                  data-slide="${index}"
                  style="height: 10px; border-radius: 5px; border: none; background: ${index === 0 ? textColor : mutedColor}; cursor: pointer; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); width: ${index === 0 ? '24px' : '10px'};">
                </button>
              `).join('')}
            </div>
          </div>

          <style>
            /* Show dots on mobile only */
            @media (max-width: 749px) {
              .pw-video-carousel-dots {
                display: flex !important;
              }
            }
          </style>

          ${this.videos.length > 3 ? `
            <!-- View All Videos Button (Bottom Right) -->
            <div style="position: absolute; bottom: var(--sp-4); right: var(--sp-4);">
              <button
                type="button"
                class="pw-view-all-videos pw-learn-more"
                aria-controls="${this.modalId}"
                aria-expanded="false"
              >
                <span>View all</span>
                <svg class="pw-learn-more__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>
          ` : ''}

        </div>
      `;
    }

    /**
     * Render individual video thumbnail card
     */
    renderVideoCard(video, index) {
      const isActive = index === 0;
      const isAssembly = this.isAssemblyVideo(video);
      const textColor = 'rgb(var(--color-drawer-background))';

      return `
        <div
          class="pw-video-card ${isActive ? 'pw-video-card--active' : ''}"
          data-video-index="${index}"
          data-slide-index="${index}"
          role="tabpanel"
          aria-hidden="${!isActive}"
          style="flex: 0 0 100%; scroll-snap-align: start; scroll-snap-stop: always;"
        >
          <div style="background: rgb(var(--color-drawer-background) / 0.08); border: 1px solid rgb(var(--color-drawer-background) / 0.15); border-radius: var(--rounded-card); overflow: hidden; transition: transform 0.2s ease, box-shadow 0.2s ease;"
            onmouseenter="this.style.transform='translateY(-4px)'; this.style.boxShadow='0 8px 24px rgba(0,0,0,0.2)'"
            onmouseleave="this.style.transform='translateY(0)'; this.style.boxShadow='none'"
          >

            <!-- Video Player Container -->
            <div class="pw-video-player-container" style="position: relative; width: 100%; aspect-ratio: 16/9; background: #000; overflow: hidden; cursor: pointer;" data-video-type="${video.type}" data-video-id="${video.videoId || ''}" data-video-url="${video.url || ''}">
              <!-- Thumbnail (will be replaced with player on click) -->
              <img
                class="pw-video-thumbnail"
                src="${this.getVideoThumbnail(video)}"
                alt="${video.title || 'Product video'}"
                style="width: 100%; height: 100%; object-fit: cover;"
                loading="lazy"
              />
              <!-- Dark overlay + play button -->
              <div class="pw-video-overlay" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; transition: background 0.2s ease;"
                onmouseenter="this.style.background='rgba(0,0,0,0.5)'"
                onmouseleave="this.style.background='rgba(0,0,0,0.4)'"
              >
                <div style="width: 4rem; height: 4rem; background: rgba(255,255,255,0.95); border-radius: 50%; display: flex; align-items: center; justify-content: center; transition: transform 0.2s ease, box-shadow 0.2s ease;"
                  onmouseenter="this.style.transform='scale(1.1)'; this.style.boxShadow='0 4px 16px rgba(0,0,0,0.3)'"
                  onmouseleave="this.style.transform='scale(1)'; this.style.boxShadow='none'"
                >
                  <svg style="width: 2rem; height: 2rem; color: #000; margin-left: 0.25rem;" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z"/>
                  </svg>
                </div>
              </div>
            </div>

            <!-- Video Title -->
            <div style="padding: var(--sp-3);">
              ${isAssembly ? `
                <span style="display: inline-block; padding: 0.25rem 0.5rem; background: rgb(var(--color-drawer-background)); color: rgb(var(--color-drawer-text)); font-size: var(--text-2xs); font-weight: 700; text-transform: uppercase; border-radius: var(--rounded-button); margin-bottom: 0.5rem;">
                  ASSEMBLY GUIDE
                </span>
              ` : ''}
              <h4 style="font-size: var(--text-sm); font-weight: 600; margin: 0; color: ${textColor}; line-height: 1.3;">
                ${video.title || video.alt || 'Product Video'}
              </h4>
            </div>

          </div>
        </div>
      `;
    }

    /**
     * Get video thumbnail URL
     */
    getVideoThumbnail(video) {
      console.log('[VideoGallery] Getting thumbnail for video:', video);

      // Prefer thumbnail from video object (from Lambda or Shopify)
      if (video.thumbnail) {
        console.log('[VideoGallery] Using Lambda/Shopify thumbnail:', video.thumbnail);
        return video.thumbnail;
      } else if (video.poster) {
        console.log('[VideoGallery] Using poster:', video.poster);
        return video.poster;
      } else if (video.type === 'youtube') {
        // Use hqdefault (high quality, always available) instead of maxresdefault (not always available)
        const thumb = `https://img.youtube.com/vi/${video.videoId}/hqdefault.jpg`;
        console.log('[VideoGallery] Using YouTube thumbnail:', thumb);
        return thumb;
      } else if (video.type === 'vimeo') {
        // For Vimeo videos from showcases, Lambda should provide thumbnail
        // Fallback to vumbnail service if not provided
        const thumb = `https://vumbnail.com/${video.videoId}.jpg`;
        console.log('[VideoGallery] Using vumbnail fallback:', thumb);
        return thumb;
      }

      console.log('[VideoGallery] Using placeholder thumbnail');
      return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="640" height="360"%3E%3Crect fill="%23000" width="640" height="360"/%3E%3Ctext x="50%25" y="50%25" fill="%23fff" font-size="20" text-anchor="middle" dominant-baseline="middle"%3EVideo%3C/text%3E%3C/svg%3E';
    }

    /**
     * Get full video URL for opening in new tab
     */
    getVideoUrl(video) {
      if (video.url) return video.url;
      if (video.type === 'youtube') return `https://www.youtube.com/watch?v=${video.videoId}`;
      if (video.type === 'vimeo') return `https://vimeo.com/${video.videoId}`;
      return '#';
    }

    /**
     * Build modal HTML with video carousel
     */
    buildModalHTML() {
      const bgColor = 'rgb(var(--color-drawer-text))';
      const textColor = 'rgb(var(--color-drawer-background))';
      const mutedColor = 'rgb(var(--color-drawer-background) / 0.7)';

      return `
        <x-modal id="${this.modalId}" class="x-modal drawer z-30 fixed bottom-0 left-0 h-full w-full pointer-events-none" role="dialog" aria-modal="true" aria-label="Product Videos" hidden>
          <overlay-element class="overlay fixed-modal invisible opacity-0 fixed bottom-0 left-0 w-full h-screen pointer-events-none" aria-controls="${this.modalId}" aria-expanded="false"></overlay-element>
          <div class="drawer__inner z-10 absolute top-0 flex flex-col w-full h-full overflow-hidden">
            <gesture-element class="drawer__header flex items-center justify-between opacity-0 invisible relative">
              <span class="drawer__title heading font-bold title-md">${this.content.cardHeading}</span>
              <button class="button button--secondary button--close drawer__close hidden sm:flex items-center justify-center" type="button" is="hover-button" aria-controls="${this.modalId}" aria-expanded="false" aria-label="Close">
                <span class="btn-fill" data-fill></span>
                <span class="btn-text">
                  <svg class="icon icon-close icon-sm" aria-hidden="true" focusable="false" role="presentation" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 14" fill="none">
                    <path d="M15 1.00012L1 13.0001M1 1L15 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                  </svg>
                </span>
              </button>
            </gesture-element>
            <div class="drawer__content opacity-0 invisible flex flex-col h-full grow shrink">
              <div class="drawer__scrollable relative flex flex-col gap-5 md:gap-7 grow shrink">

                <!-- Header (Dark theme like personalization) -->
                <div style="background: ${bgColor}; border-radius: var(--rounded-card); padding: var(--sp-5); position: relative; overflow: hidden;">
                  <div style="display: flex; align-items: flex-start; gap: var(--sp-3);">
                    <div style="display: flex; align-items: center; justify-content: center; width: 2.5rem; height: 2.5rem; background: rgb(var(--color-drawer-background) / 0.15); border-radius: var(--rounded-card); flex-shrink: 0;">
                      ${this.getVideoIconSVG(textColor)}
                    </div>
                    <div>
                      <h3 style="font-family: var(--font-heading-family); font-weight: var(--font-heading-weight); font-size: var(--text-lg); line-height: 1.2; margin: 0 0 var(--sp-1) 0; color: ${textColor};">
                        ${this.content.modalHeading}
                      </h3>
                      <p style="font-size: var(--text-sm); line-height: 1.5; color: ${mutedColor}; margin: 0;">
                        ${this.content.modalSubheading}
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Video Carousel -->
                <div class="pw-video-carousel-track" style="display: flex; overflow-x: auto; overflow-y: hidden; scroll-snap-type: x mandatory; scroll-behavior: smooth; gap: var(--sp-4); border-radius: var(--rounded-card); -webkit-overflow-scrolling: touch; scrollbar-width: none; -ms-overflow-style: none;">
                  ${this.videos.map((video, index) => this.buildVideoSlide(video, index)).join('')}
                </div>

                ${this.videos.length > 1 ? `
                  <!-- Carousel Dots (Mobile only) -->
                  <div class="pw-video-carousel-dots pw-video-modal-dots" role="tablist" aria-label="Video navigation" style="display: none; gap: var(--sp-2); align-items: center; justify-content: center;">
                    ${this.videos.map((_, index) => `
                      <button
                        class="pw-video-dot ${index === 0 ? 'pw-video-dot--active' : ''}"
                        aria-label="Go to video ${index + 1}"
                        data-slide="${index}"
                        style="height: 10px; border-radius: 5px; border: none; background: ${index === 0 ? 'rgb(var(--color-base-button))' : 'rgb(var(--color-drawer-text) / 0.3)'}; cursor: pointer; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); width: ${index === 0 ? '24px' : '10px'};">
                      </button>
                    `).join('')}
                  </div>

                  <style>
                    /* Show modal dots on mobile only */
                    @media (max-width: 749px) {
                      .pw-video-modal-dots {
                        display: flex !important;
                      }
                    }
                  </style>
                ` : ''}

              </div>
            </div>
          </div>
        </x-modal>
      `;
    }

    /**
     * Build individual video slide
     */
    buildVideoSlide(video, index) {
      const isActive = index === 0;
      const isAssembly = this.isAssemblyVideo(video);

      return `
        <div
          class="pw-video-slide ${isActive ? 'pw-video-slide--active' : ''}"
          data-slide-index="${index}"
          style="flex: 0 0 100%; scroll-snap-align: start; scroll-snap-stop: always;">

          <div style="background: rgb(var(--color-drawer-text) / 0.02); border: 1px solid rgb(var(--color-drawer-text) / 0.08); border-radius: var(--rounded-card); overflow: hidden;">

            <!-- Video Player -->
            <div style="position: relative; width: 100%; background: #000; aspect-ratio: 16/9;">
              ${this.renderVideoPlayer(video)}
            </div>

            <!-- Video Info -->
            <div style="padding: var(--sp-4);">
              ${isAssembly ? `
                <span style="display: inline-block; padding: 0.25rem 0.5rem; background: rgb(var(--color-base-button)); color: rgb(var(--color-base-button-text)); font-size: var(--text-2xs); font-weight: 700; text-transform: uppercase; border-radius: var(--rounded-button); margin-bottom: 0.5rem;">
                  ASSEMBLY GUIDE
                </span>
              ` : ''}
              <h4 style="font-size: var(--text-base); font-weight: 600; margin: 0; color: rgb(var(--color-drawer-text));">
                ${video.title || video.alt || `Video ${index + 1}`}
              </h4>
            </div>

          </div>
        </div>
      `;
    }

    /**
     * Render video player based on type (for modal)
     */
    renderVideoPlayer(video) {
      if (video.type === 'youtube') {
        return `
          <iframe
            width="100%"
            height="100%"
            src="https://www.youtube.com/embed/${video.videoId}?rel=0"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
            style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"
          ></iframe>
        `;
      } else if (video.type === 'vimeo') {
        return `
          <iframe
            width="100%"
            height="100%"
            src="https://player.vimeo.com/video/${video.videoId}?title=0&byline=0&portrait=0"
            frameborder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowfullscreen
            style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"
          ></iframe>
        `;
      } else if (video.type === 'vimeo-showcase') {
        return `
          <iframe
            width="100%"
            height="100%"
            src="https://vimeo.com/showcase/${video.showcaseId || video.videoId}/embed"
            frameborder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowfullscreen
            style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"
          ></iframe>
        `;
      } else if (video.type === 'shopify') {
        return `
          <video
            controls
            playsinline
            preload="metadata"
            style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: contain;"
            poster="${video.poster || ''}"
          >
            <source src="${video.url}" type="video/mp4">
            Your browser does not support the video tag.
          </video>
        `;
      }

      return `<p style="color: white; text-align: center; padding: 2rem;">Video format not supported</p>`;
    }

    /**
     * Attach event listeners
     */
    attachEventListeners() {
      const track = this.container.querySelector('.pw-video-carousel-track');
      const dots = this.container.querySelectorAll('.pw-video-carousel-dot');
      const playPauseBtn = this.container.querySelector('.pw-video-play-pause');

      // Video card click handlers - play inline AND pause auto-play
      const videoContainers = this.container.querySelectorAll('.pw-video-player-container');
      videoContainers.forEach(container => {
        container.addEventListener('click', (e) => {
          this.pauseAutoPlay(); // Stop rotation when user clicks video

          // Check if this video is already playing
          const isPlaying = container.classList.contains('pw-video-playing');

          if (isPlaying) {
            // Stop this video
            this.stopVideo(container);
          } else {
            // Stop all other videos first
            this.stopAllVideos();
            // Play this video
            this.playVideoInline(container);
          }
        });
      });

      // Dot indicators
      dots.forEach(dot => {
        dot.addEventListener('click', (e) => {
          const slideIndex = parseInt(e.target.dataset.slide);
          this.goToSlide(slideIndex);
        });
      });

      // Play/Pause button removed (autoplay disabled)
      // Hover listeners removed (no autoplay to pause)

      // Intersection Observer for dot updates
      if (track && 'IntersectionObserver' in window) {
        const slides = this.container.querySelectorAll('.pw-video-card');

        const observerOptions = {
          root: track,
          threshold: 0.5
        };

        this.slideObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const slideIndex = parseInt(entry.target.dataset.slideIndex);
              this.updateCurrentSlide(slideIndex);
            }
          });
        }, observerOptions);

        slides.forEach(slide => this.slideObserver.observe(slide));
      }

      // "View all videos" button (if more than 3 videos)
      const viewAllBtn = this.container.querySelector('.pw-view-all-videos');
      if (viewAllBtn) {
        viewAllBtn.addEventListener('click', () => this.openModal());
      }

      // Autoplay disabled - removed startAutoPlay() call
    }

    /**
     * Start auto-play rotation
     */
    startAutoPlay() {
      if (this.autoPlayInterval) {
        clearInterval(this.autoPlayInterval);
      }

      this.autoPlayInterval = setInterval(() => {
        if (this.isAutoPlaying) {
          this.nextSlide();
        }
      }, this.autoPlayDelay);
    }

    /**
     * Stop auto-play
     */
    stopAutoPlay() {
      if (this.autoPlayInterval) {
        clearInterval(this.autoPlayInterval);
        this.autoPlayInterval = null;
      }
    }

    /**
     * Toggle auto-play
     */
    toggleAutoPlay() {
      if (this.isAutoPlaying) {
        this.pauseAutoPlay();
      } else {
        this.resumeAutoPlay();
      }
    }

    /**
     * Pause auto-play
     */
    pauseAutoPlay() {
      this.isAutoPlaying = false;
      this.updatePlayPauseButton();
    }

    /**
     * Resume auto-play
     */
    resumeAutoPlay() {
      this.isAutoPlaying = true;
      this.updatePlayPauseButton();
    }

    /**
     * Update play/pause button UI
     */
    updatePlayPauseButton() {
      const pauseIcon = this.container.querySelector('.pw-video-pause-icon');
      const playIcon = this.container.querySelector('.pw-video-play-icon');

      if (!pauseIcon || !playIcon) return;

      if (this.isAutoPlaying) {
        pauseIcon.style.display = 'block';
        playIcon.style.display = 'none';
      } else {
        pauseIcon.style.display = 'none';
        playIcon.style.display = 'block';
      }
    }

    /**
     * Go to next slide
     */
    nextSlide() {
      const nextIndex = (this.currentSlide + 1) % this.videos.length;
      this.goToSlide(nextIndex);
    }

    /**
     * Go to previous slide
     */
    previousSlide() {
      const prevIndex = (this.currentSlide - 1 + this.videos.length) % this.videos.length;
      this.goToSlide(prevIndex);
    }

    /**
     * Go to specific slide
     */
    goToSlide(index) {
      if (index === this.currentSlide) return;

      const track = this.container.querySelector('.pw-video-carousel-track');
      const slides = this.container.querySelectorAll('.pw-video-card');
      const slide = slides[index];

      if (slide && track) {
        const slideOffset = slide.offsetLeft;
        track.scrollTo({
          left: slideOffset,
          behavior: 'smooth'
        });
      }
    }

    /**
     * Update current slide indicator
     */
    updateCurrentSlide(index) {
      if (index === this.currentSlide) return;

      const slides = this.container.querySelectorAll('.pw-video-card');
      const dots = this.container.querySelectorAll('.pw-video-carousel-dot');

      // Update slides
      slides[this.currentSlide]?.classList.remove('pw-video-card--active');
      slides[this.currentSlide]?.setAttribute('aria-hidden', 'true');

      slides[index]?.classList.add('pw-video-card--active');
      slides[index]?.setAttribute('aria-hidden', 'false');

      // Update dots
      dots.forEach((dot, i) => {
        if (i === index) {
          dot.classList.add('pw-video-carousel-dot--active');
          dot.style.width = '24px';
          dot.style.background = 'rgb(var(--color-drawer-background))';
          dot.setAttribute('aria-selected', 'true');
        } else {
          dot.classList.remove('pw-video-carousel-dot--active');
          dot.style.width = '10px';
          dot.style.background = 'rgb(var(--color-drawer-background) / 0.7)';
          dot.setAttribute('aria-selected', 'false');
        }
      });

      this.currentSlide = index;
    }

    /**
     * Stop all currently playing videos
     */
    stopAllVideos() {
      const allContainers = this.container.querySelectorAll('.pw-video-player-container');
      allContainers.forEach(container => {
        this.stopVideo(container);
      });
    }

    /**
     * Stop a specific video and restore thumbnail
     */
    stopVideo(container) {
      const iframe = container.querySelector('iframe, video');
      if (iframe) {
        // Get original thumbnail HTML
        const videoType = container.dataset.videoType;
        const videoId = container.dataset.videoId;
        const thumbnail = this.getVideoThumbnail(videoType, videoId, container.dataset.videoUrl);

        // Restore thumbnail
        container.innerHTML = `
          <img
            src="${thumbnail}"
            alt="Video thumbnail"
            style="width: 100%; height: 100%; object-fit: cover; border-radius: var(--rounded-card);"
            loading="lazy"
          />
          <div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); width: 48px; height: 48px; background: rgba(255, 255, 255, 0.95); border-radius: 50%; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);">
            <svg style="width: 24px; height: 24px; color: #000; margin-left: 2px;" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z"></path>
            </svg>
          </div>
        `;

        container.classList.remove('pw-video-playing');
      }
    }

    /**
     * Play video inline - replace thumbnail with iframe
     */
    playVideoInline(container) {
      console.log('[VideoGallery] Playing video inline');
      const videoType = container.dataset.videoType;
      const videoId = container.dataset.videoId;
      const videoUrl = container.dataset.videoUrl;

      console.log('[VideoGallery] Video data:', { videoType, videoId, videoUrl });

      let iframeHTML = '';

      if (videoType === 'youtube') {
        iframeHTML = `
          <iframe
            width="100%"
            height="100%"
            src="https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&rel=0"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
            style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"
          ></iframe>
        `;
      } else if (videoType === 'vimeo') {
        iframeHTML = `
          <iframe
            width="100%"
            height="100%"
            src="https://player.vimeo.com/video/${videoId}?autoplay=1&muted=1&title=0&byline=0&portrait=0"
            frameborder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowfullscreen
            style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"
          ></iframe>
        `;
      } else if (videoType === 'vimeo-showcase') {
        iframeHTML = `
          <iframe
            width="100%"
            height="100%"
            src="https://vimeo.com/showcase/${videoId}/embed?autoplay=1&muted=1"
            frameborder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowfullscreen
            style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"
          ></iframe>
        `;
      } else if (videoType === 'shopify' && videoUrl) {
        iframeHTML = `
          <video
            controls
            autoplay
            muted
            playsinline
            preload="metadata"
            style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: contain;"
          >
            <source src="${videoUrl}" type="video/mp4">
            Your browser does not support the video tag.
          </video>
        `;
      }

      console.log('[VideoGallery] Generated iframe HTML:', iframeHTML.substring(0, 100));

      if (iframeHTML) {
        container.innerHTML = iframeHTML;
        container.classList.add('pw-video-playing');
        console.log('[VideoGallery] Video player loaded successfully');
      } else {
        console.error('[VideoGallery] No iframe HTML generated - unsupported video type?');
      }
    }

    /**
     * Go to specific slide in modal carousel
     */
    goToModalSlide(index) {
      const modal = document.getElementById(this.modalId);
      if (!modal) return;

      const track = modal.querySelector('.pw-video-carousel-track');
      const slides = modal.querySelectorAll('.pw-video-slide');
      const slide = slides[index];

      if (slide && track) {
        const slideOffset = slide.offsetLeft;
        track.scrollTo({
          left: slideOffset,
          behavior: 'smooth'
        });
      }
    }

    /**
     * Open modal
     */
    openModal() {
      const modal = document.getElementById(this.modalId);
      if (modal && modal.show) {
        modal.show();
      }
    }

    /**
     * SVG Icons
     */

    /**
     * Get video camera icon
     */
    getVideoIconSVG(colorRGB = 'var(--color-base-button)') {
      return `
        <svg style="width: 20px; height: 20px; color: rgb(${colorRGB});" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/>
        </svg>
      `;
    }

    /**
     * Get play icon
     */
    getPlayIconSVG() {
      return `
        <svg style="width: 16px; height: 16px; flex-shrink: 0; color: rgb(var(--color-base-button));" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z"/>
        </svg>
      `;
    }

    /**
     * Get check icon
     */
    getCheckIconSVG() {
      return `
        <svg style="flex-shrink: 0; width: 18px; height: 18px; color: rgb(var(--color-base-button));" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/>
        </svg>
      `;
    }
  }

  // Register widget with ProductWidgets system
  if (window.ProductWidgets && window.ProductWidgets.widgets) {
    window.ProductWidgets.widgets.VideoGalleryWidget = VideoGalleryWidget;
  } else {
    console.error('[ProductWidgets] ProductWidgets system not available');
  }

})(window);
